import { VisitDetail } from '@/components/CarePage/VisitDetail'
import { ErrorState } from '@/components/ErrorState'
import ProtectedPage from '@/components/ProtectedPage'
import { useVisitRecords } from '@/features/care/useVisitRecords'
import { FlowHeader } from '@/ui/core/flow-header'
import { PageShell } from '@/ui/core/page-shell'
import { Screen } from '@/ui/core/screen'
import { Skeleton } from '@/ui/core/skeleton'
import { router, useLocalSearchParams } from 'expo-router'
import { useMemo } from 'react'
import { View } from 'react-native'
import Images from '#/assets/images/image-map'

const goBack = () => (router.canGoBack() ? router.back() : router.replace('/care'))

/**
 * A record that is not on this member's history.
 *
 * Reachable from a stale link or a visit that has since been amended away. It sends them to Care
 * rather than offering a retry, because there is nothing about this page that a second attempt
 * would fix.
 */
const NotFound = () => (
	<ErrorState
		kind="gone"
		glyph="calendar"
		title="We can't find that visit"
		line="It may have been amended or removed. Every visit we have for you is on the Care tab."
		onAction={goBack}
	/>
)

/**
 * One past visit, as its own screen.
 *
 * Its own route rather than a sheet: a visit's record is a destination — the thing a member came
 * to Care to read — and a sheet is for a short task you return from. It also means a visit has a
 * URL, so a push notification about a released result can land straight on the visit it belongs
 * to rather than on the tab.
 *
 * `/visits/[id]` is the upcoming visit — confirm, check in, how to arrive. This is what is left
 * once a visit has happened, which is a different page about the same appointment.
 */
export default function VisitRecordScreen() {
	const { id } = useLocalSearchParams<{ id: string }>()
	const appointmentId = useMemo(() => Number.parseInt(id ?? '', 10), [id])
	const { visits, isLoading } = useVisitRecords()

	const visit = visits.find((candidate) => candidate.appointmentId === appointmentId)

	return (
		<ProtectedPage>
			<Screen preset="fixed" className="flex flex-1" safeAreaEdges={['top']}>
				<FlowHeader onBack={goBack} />
				<View className="flex-1">
					<PageShell photo={Images.BrandPhoto}>
						<Skeleton isLoading={isLoading} className="h-40 w-full">
							{visit ? <VisitDetail visit={visit} /> : <NotFound />}
						</Skeleton>
					</PageShell>
				</View>
			</Screen>
		</ProtectedPage>
	)
}
